import React, { useState, useEffect, useCallback, useRef } from 'react';
import { TextField, Paper, List, ListItem, ListItemText, CircularProgress } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import styled from 'styled-components';
import axios from 'axios';
import debounce from 'lodash/debounce';

const SearchContainer = styled.div`
  width: 100%;
  position: relative;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 8px;
`;

const NoOptionsMessage = styled.div`
  padding: 8px 16px;
  color: rgba(0, 0, 0, 0.6);
`;

const MetabaseSearch = ({
  label,
  placeholder,
  searchEndpoint,
  responseDataField,
  valueKey = 'id',
  labelKey = 'name',
  selectedHandler,
  onRemove,
  selected,
  style,
  searchParamName = 'key'
}) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const [error, setError] = useState(null);

  // Önceki isteği takip etmek için ref kullanıyoruz
  const cancelTokenRef = useRef(null);
  const initialLoadRef = useRef(false);

  // API yanıtından options'ları güvenli bir şekilde çıkaran yardımcı fonksiyon
  const extractOptions = (responseData) => {
    try {
      if (!responseData?.data) return [];

      let result = responseData.data;

      // responseDataField bir dizi ise (örn: "packets.records")
      if (responseDataField.includes('.')) {
        const fields = responseDataField.split('.');
        for (const field of fields) {
          if (!result || !result[field]) return [];
          result = result[field];
        }
      } else {
        result = result[responseDataField];
      }

      // Sonucu diziye çevir
      if (!result) return [];
      if (Array.isArray(result)) return result;
      if (typeof result === 'object') return [result];

      return [];
    } catch (error) {
      console.error('Veri çıkarma hatası:', error);
      return [];
    }
  };

  const fetchResults = useCallback(
    debounce(async (text, isInitialLoad = false) => {
      try {
        // Önceki isteği iptal et
        if (cancelTokenRef.current) {
          cancelTokenRef.current.cancel('Yeni istek yapıldı');
        }

        // Yeni bir cancel token oluştur
        cancelTokenRef.current = axios.CancelToken.source();

        if (!isInitialLoad && !text?.trim()) {
          setOptions([]);
          setLoading(false);
          return;
        }

        setLoading(true);
        setError(null);

        const params = new URLSearchParams();
        if (text?.trim()) {
          params.append(searchParamName, text);
        }

        // Sayfalama gerektiren sayfalar için özel durum
        if (searchEndpoint.includes('/company/packets') ||
          searchParamName === 'stockname' ||
          searchParamName === 'name') {
          params.append('page', '1');
        }

        const response = await axios.get(
          `${searchEndpoint}${params.toString() ? '?' + params.toString() : ''}`,
          {
            cancelToken: cancelTokenRef.current.token
          }
        );

        const extractedOptions = extractOptions(response.data);
        console.log('Extracted options:', extractedOptions); // Debug için
        setOptions(Array.isArray(extractedOptions) ? extractedOptions : []);

        if (isInitialLoad) {
          setInitialDataLoaded(true);
          initialLoadRef.current = true;
        }

      } catch (err) {
        if (!axios.isCancel(err)) {
          console.error('Arama sırasında hata oluştu:', err);
          setError(err.message);
          setOptions([]);
        }
      } finally {
        if (cancelTokenRef.current) {
          setLoading(false);
        }
      }
    }, 300),
    [searchEndpoint, responseDataField, searchParamName]
  );

  // Component unmount olduğunda aktif isteği iptal et
  useEffect(() => {
    return () => {
      if (cancelTokenRef.current) {
        cancelTokenRef.current.cancel('Component unmounted');
      }
    };
  }, []);

  // Sadece component ilk mount olduğunda verileri getir
  useEffect(() => {
    if (!initialLoadRef.current) {
      fetchResults('', true);
    }
  }, [fetchResults]);

  const handleInputChange = (event, value) => {
    setSearchText(value);
    setError(null);
    setLoading(true);

    if (value?.trim()) {
      fetchResults(value);
    } else {
      setOptions([]);
      setLoading(false);
    }
  };

  const handleChange = (event, value) => {
    if (value) {
      selectedHandler(value);
    } else {
      onRemove();
    }
    setSearchText('');
    setError(null);
  };

  const getNoOptionsText = () => {
    if (loading) {
      return 'Aranıyor...';
    }
    if (error) {
      return 'Bir hata oluştu';
    }
    if (!searchText?.trim()) {
      return 'Aramak için yazın...';
    }
    return 'Sonuç bulunamadı';
  };

  return (
    <SearchContainer style={style}>
      <Autocomplete
        open={open}
        onOpen={() => {
          setOpen(true);
          setError(null);
        }}
        onClose={() => setOpen(false)}
        getOptionSelected={(option, value) => option?.[valueKey] === value?.[valueKey]}
        getOptionLabel={(option) => option?.[labelKey] || ''}
        options={options || []}
        loading={loading}
        value={selected}
        onChange={handleChange}
        onInputChange={handleInputChange}
        inputValue={searchText}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={placeholder}
            variant="outlined"
            size="small"
            error={!!error}
            helperText={error}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(option) => (
          <ListItem>
            <ListItemText
              primary={option?.[labelKey] || ''}
              secondary={option?.description || option?.code || null}
            />
          </ListItem>
        )}
        noOptionsText={getNoOptionsText()}
      />
    </SearchContainer>
  );
};

export default MetabaseSearch; 